import React, { useState, useEffect } from 'react';
import { getSocialLinks, getProfile } from '../services/api';
import { Linkedin, Github, Instagram, ExternalLink, Mail } from 'lucide-react';
import ContactModal from './ContactModal';

// X (Twitter) icon component
const XIcon = ({ size = 24, className = '' }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const Footer = () => {
  const [socialLinks, setSocialLinks] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [linksData, profileData] = await Promise.all([
          getSocialLinks(),
          getProfile()
        ]);
        setSocialLinks(linksData);
        setProfile(profileData);
      } catch (error) {
        console.error('Error fetching footer data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <footer id="contact" className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-16 relative overflow-hidden">
        <div className="container mx-auto px-6 md:px-12 relative z-10 text-center">
          <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </footer>
    );
  }

  const socialIcons = [
    { icon: Linkedin, url: socialLinks?.linkedin, label: 'LinkedIn' },
    { icon: Github, url: socialLinks?.github, label: 'GitHub' },
    { icon: XIcon, url: socialLinks?.twitter, label: 'X' },
    { icon: Instagram, url: socialLinks?.instagram, label: 'Instagram' },
  ];

  const links = [
    { label: 'Medium Articles', url: socialLinks?.medium },
    { label: 'Republic Profile', url: socialLinks?.republic },
    { label: 'OpenSea', url: socialLinks?.opensea },
  ];

  return (
    <>
      <footer id="contact" className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-950 text-white py-16 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-slate-700/20 to-slate-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-amber-500/10 to-amber-400/5 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* Main content */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12">
              {/* Left side - Contact */}
              <div>
                <h3 className="text-4xl font-bold tracking-tight mb-6 text-white">
                  Let's Connect
                </h3>
                <p className="text-slate-400 font-light leading-relaxed mb-8">
                  Whether you want to discuss technology, explore investment opportunities, 
                  or just chat about pickleball — I'd love to hear from you.
                </p>
                <button
                  onClick={() => setShowContactModal(true)}
                  className="flex items-center gap-3 px-6 py-3 bg-slate-700 text-white font-semibold rounded-xl hover:bg-slate-600 transition-all duration-300 group shadow-lg hover:shadow-xl border border-slate-600"
                >
                  <Mail size={20} />
                  <span>Get in touch</span>
                  <ExternalLink size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                </button>
              </div>

              {/* Right side - Links */}
              <div>
                <h3 className="text-2xl font-light tracking-tight mb-6">
                  Explore More
                </h3>
                <ul className="space-y-4 mb-8">
                  {links.map((link, index) => (
                    link.url && (
                      <li key={index}>
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center gap-2 group"
                        >
                          <span>{link.label}</span>
                          <ExternalLink size={14} className="opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
                        </a>
                      </li>
                    )
                  ))}
                </ul>

                {/* Social Icons */}
                <div className="flex gap-4">
                  {socialIcons.map((social, index) => {
                    const Icon = social.icon;
                    return (
                      social.url && (
                        <a
                          key={index}
                          href={social.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={social.label}
                          className="w-12 h-12 border border-gray-600 flex items-center justify-center text-gray-400 hover:border-white hover:text-white hover:scale-110 transition-all duration-300"
                        >
                          <Icon size={20} />
                        </a>
                      )
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-slate-700 pt-8">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <p className="text-slate-500 text-sm font-light">
                  © {new Date().getFullYear()} {profile?.name || 'Nishit Sadhwani'}. Built with passion.
                </p>
                <div className="flex items-center gap-4">
                  <a 
                    href="/privacy" 
                    className="text-slate-500 hover:text-slate-300 text-sm font-light transition-colors"
                  >
                    Privacy Policy
                  </a>
                  <span className="text-slate-700">·</span>
                  <p className="text-slate-600 text-sm font-light">
                    {profile?.location || 'McKinney, Texas'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      <ContactModal isOpen={showContactModal} onClose={() => setShowContactModal(false)} />
    </>
  );
};

export default Footer;